import type {RefObject} from 'react';
import {useCallback, useRef, useEffect} from 'react';

import {useTranslation} from 'react-i18next';

import {usePrevious} from '@pexip/hooks';

import {sidePanelPlusDesktopToolbarWidth} from '../constants';
import {getLangDirection} from '../utils/getLangDirection';

export const isOverlappingSidePanel = (
    elementRect: DOMRect,
    dir: 'ltr' | 'rtl',
) => {
    if (dir === 'ltr') {
        return Math.floor(elementRect.left) <= sidePanelPlusDesktopToolbarWidth;
    }
    return (
        Math.ceil(elementRect.right) >=
        window.innerWidth - sidePanelPlusDesktopToolbarWidth
    );
};

export const useSidePanelTransform = (
    isSidePanelVisible: boolean,
    elementRef: RefObject<HTMLDivElement | null>,
    enabled = true,
) => {
    const prevIsSidePanelVisible = usePrevious(isSidePanelVisible);
    const lastLeft = useRef<number | undefined>(undefined);
    const movedByPanel = useRef(false);

    const setNoSidePanelLeft = useCallback((left?: number) => {
        lastLeft.current = left;
        movedByPanel.current = false;
    }, []);

    const {i18n} = useTranslation();
    const dir = getLangDirection(i18n);

    useEffect(() => {
        if (isSidePanelVisible === prevIsSidePanelVisible) {
            return;
        }
        if (enabled && elementRef.current) {
            const element = elementRef.current;
            if (isSidePanelVisible) {
                const elementRect = element.getBoundingClientRect();
                if (isOverlappingSidePanel(elementRect, dir)) {
                    movedByPanel.current = true;
                    if (dir === 'ltr') {
                        element.style.setProperty(
                            'translate',
                            `${
                                sidePanelPlusDesktopToolbarWidth -
                                elementRect.left
                            }px 0px`,
                        );
                    } else {
                        element.style.setProperty(
                            'translate',
                            `${
                                window.innerWidth -
                                sidePanelPlusDesktopToolbarWidth -
                                elementRect.right
                            }px 0px`,
                        );
                    }
                }
            } else if (movedByPanel.current) {
                element.style.setProperty('translate', '0px 0px');
                movedByPanel.current = false;
                lastLeft.current = undefined;
            } else if (lastLeft.current !== undefined) {
                const elementRect = element.getBoundingClientRect();
                if (dir === 'ltr') {
                    const delta = lastLeft.current - elementRect.left;
                    element.style.setProperty('translate', `${delta}px 0px`);
                } else {
                    const delta =
                        window.innerWidth -
                        lastLeft.current -
                        elementRect.right;
                    element.style.setProperty('translate', `${delta}px 0px`);
                }
                lastLeft.current = undefined;
            }
        }
    }, [enabled, elementRef, isSidePanelVisible, prevIsSidePanelVisible, dir]);

    return setNoSidePanelLeft;
};
